import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ActivateRelationTypeDocument,
  type ActivateRelationTypeMutation,
  type ActivateRelationTypeMutationVariables,
  DeactivateRelationTypeDocument,
  type DeactivateRelationTypeMutation,
  type DeactivateRelationTypeMutationVariables,
  GetRelationTypesDocument,
} from "~/features/admin/relationTypes/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Activate / deactivate a relation type (tenant-scoped, V1 parity). Returns
 * plain submitters and a `processing` flag; the calling view owns the confirm
 * dialog. Refetches the list so the deactivated marker updates.
 */
export function useRelationTypeActivation() {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const [activateRelationType] = useMutation<ActivateRelationTypeMutation, ActivateRelationTypeMutationVariables>(
    ActivateRelationTypeDocument,
    { refetchQueries: [{ query: GetRelationTypesDocument, variables: { tenantId } }] },
  );
  const [deactivateRelationType] = useMutation<DeactivateRelationTypeMutation, DeactivateRelationTypeMutationVariables>(
    DeactivateRelationTypeDocument,
    { refetchQueries: [{ query: GetRelationTypesDocument, variables: { tenantId } }] },
  );

  const onActivate = useCallback(
    async ({ id }: { id: string }) => {
      setProcessing(true);
      try {
        await activateRelationType({ variables: { input: { tenantId, id } } });
        toast.success(t("feature.common.activatedSuccessfully", { model: "relationType" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [activateRelationType, tenantId, t],
  );

  const onDeactivate = useCallback(
    async ({ id }: { id: string }) => {
      setProcessing(true);
      try {
        await deactivateRelationType({ variables: { input: { tenantId, id } } });
        toast.success(t("feature.common.deactivatedSuccessfully", { model: "relationType" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [deactivateRelationType, tenantId, t],
  );

  return { onActivate, onDeactivate, processing };
}
